// frontend/src/components/Navbar.jsx
// ENHANCED NAVBAR - Best-in-class user experience with modern features
// Preserves existing functionality while adding powerful admin & mobile features

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Menu, X, ChevronDown, Shield, User, 
  BarChart3, Bug, Sun, Moon,
  Home, Award,
  Activity, Eye, RefreshCw, Zap,
  LogOut, CreditCard, HelpCircle
} from 'lucide-react';

import api from '../services/api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import ProfileAvatar from './ProfileAvatar';
import '../css/Navbar.css';

// Enhanced Bug Report Modal with auto-captured diagnostics
function BugReportModal({ isOpen, onClose }) {
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('idle');
  const [diagnostics, setDiagnostics] = useState(null);
  const textareaRef = useRef(null);
  const { user, roles, overrideRoles } = useAuth();

  // Capture system diagnostics when modal opens
  useEffect(() => {
    if (isOpen) {
      const captureDiagnostics = () => {
        const diagData = {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          screen: {
            width: window.screen.width,
            height: window.screen.height,
            devicePixelRatio: window.devicePixelRatio
          },
          user: {
            userId: user?.userId,
            email: user?.email,
            username: user?.username,
            actualRoles: user?.roles,
            effectiveRoles: roles,
            viewingAs: overrideRoles ? overrideRoles[0] : null,
            subscriptionStatus: user?.subscription_status
          },
          browser: {
            language: navigator.language,
            cookieEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,
            platform: navigator.platform
          },
          performance: performance.now(),
          localStorage: {
            hasToken: !!localStorage.getItem('accessToken'),
            sessionToken: !!sessionStorage.getItem('accessToken')
          },
          errors: window.__reactErrors || [], // Capture any React errors if available
          console: window.__consoleErrors?.slice(-5) || [] // Last 5 console errors
        };
        setDiagnostics(diagData);
      };

      captureDiagnostics();
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [isOpen, user, roles, overrideRoles]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!description.trim()) return;

    setStatus('sending');
    try {
      const reportData = {
        description: description.trim(),
        diagnostics,
        reportedAt: new Date().toISOString()
      };
      
      await api.post('/bug-report', reportData);
      setStatus('sent');
      setTimeout(() => {
        onClose();
        setDescription('');
        setStatus('idle');
        setDiagnostics(null);
      }, 2000);
    } catch (error) {
      console.error('Bug report submission failed:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  }, [description, diagnostics, onClose]);

  if (!isOpen) return null;

  return (
    <div className="bug-modal-overlay">
      <div className="bug-modal">
        <div className="bug-modal-content">
          <div className="bug-modal-header">
            <div className="bug-modal-title-group">
              <div className="bug-modal-icon-wrapper">
                <Bug className="bug-modal-icon" />
              </div>
              <h3 className="bug-modal-title">Report Bug</h3>
            </div>
            <button onClick={onClose} className="bug-modal-close">
              <X className="bug-modal-close-icon" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="bug-modal-form">
            <div className="bug-modal-field">
              <label className="bug-modal-label">
                Describe the issue you encountered
              </label>
              <textarea
                ref={textareaRef}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Please describe what went wrong and the steps to reproduce..."
                className="bug-modal-textarea"
                disabled={status === 'sending'}
              />
            </div>

            {/* Diagnostics Info */}
            {diagnostics && (
              <div className="bug-modal-diagnostics">
                <div className="bug-modal-diagnostics-header">
                  <span className="bug-modal-diagnostics-title">Automatic diagnostics captured:</span>
                  <span className="bug-modal-diagnostics-count">
                    {Object.keys(diagnostics).length} data points
                  </span>
                </div>
                <div className="bug-modal-diagnostics-preview">
                  <div>📍 Page: {diagnostics.pathname}</div>
                  <div>👤 User: {diagnostics.user.username} ({diagnostics.user.effectiveRoles.join(', ')})</div>
                  <div>🖥️ Device: {diagnostics.viewport.width}×{diagnostics.viewport.height}</div>
                  <div>🕐 Time: {new Date(diagnostics.timestamp).toLocaleString()}</div>
                  {diagnostics.user.viewingAs && (
                    <div>👁️ Viewing as: {diagnostics.user.viewingAs}</div>
                  )}
                </div>
              </div>
            )}

            <div className="bug-modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="bug-modal-btn bug-modal-btn-cancel"
                disabled={status === 'sending'}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!description.trim() || status === 'sending'}
                className="bug-modal-btn bug-modal-btn-submit"
              >
                {status === 'sending' && <div className="bug-modal-spinner" />}
                {status === 'sending' ? 'Sending...' : 'Submit Report'}
              </button>
            </div>

            {status === 'sent' && (
              <div className="bug-modal-status success">
                <div className="bug-modal-status-dot" />
                Your report has been submitted.
              </div>
            )}

            {status === 'error' && (
              <div className="bug-modal-status error">
                <div className="bug-modal-status-dot" />
                Failed to submit. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// Enhanced Role Selector with improved UX
function RoleSelector({ roles, overrideRoles, setOverrideRoles, isAdmin }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const VIEW_ROLES = useMemo(() => [
    { value: 'Admin', label: 'Admin', icon: Shield, color: 'text-purple-600 dark:text-purple-400' },
    { value: 'VIP', label: 'VIP', icon: Award, color: 'text-yellow-600 dark:text-yellow-400' },
    { value: 'Gifted', label: 'Gifted', icon: Zap, color: 'text-green-600 dark:text-green-400' },
    { value: 'Standard', label: 'Standard', icon: User, color: 'text-blue-600 dark:text-blue-400' },
    { value: 'Trial', label: 'Trial', icon: Activity, color: 'text-indigo-600 dark:text-indigo-400' },
    { value: 'Suspended', label: 'Suspended', icon: Eye, color: 'text-orange-600 dark:text-orange-400' },
    { value: 'Banned', label: 'Banned', icon: X, color: 'text-red-600 dark:text-red-400' }
  ], []);

  const canonRole = useCallback((r) => {
    if (!r) return null;
    const lower = String(r).toLowerCase();
    return VIEW_ROLES.find(x => x.value.toLowerCase() === lower)?.value || String(r);
  }, [VIEW_ROLES]);

  const currentRole = useMemo(() => {
    const current = overrideRoles ? overrideRoles[0] : (roles[0] || 'Admin');
    return canonRole(current) || 'Admin';
  }, [overrideRoles, roles, canonRole]);

  const currentRoleData = useMemo(() => 
    VIEW_ROLES.find(r => r.value === currentRole) || VIEW_ROLES[0], 
    [VIEW_ROLES, currentRole]
  );

  const handleRoleChange = useCallback((newRole) => {
    if (newRole === roles[0]) {
      setOverrideRoles(null); // Reset to actual role
    } else {
      setOverrideRoles([newRole]);
    }
    setIsOpen(false);
  }, [roles, setOverrideRoles]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAdmin) return null;

  return (
    <div className="role-selector" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="role-btn"
      >
        <currentRoleData.icon className={`role-icon ${currentRoleData.color}`} />
        <span>View as: {currentRole}</span>
        <ChevronDown className={`role-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="role-dropdown">
          {VIEW_ROLES.map((role) => {
            const IconComponent = role.icon;
            const isSelected = role.value === currentRole;
            const isActualRole = role.value === roles[0];

            return (
              <button
                key={role.value}
                onClick={() => handleRoleChange(role.value)}
                className={`role-option ${isSelected ? 'selected' : ''}`}
              >
                <IconComponent className={`role-icon ${role.color}`} />
                <span className="flex-1 text-left">{role.label}</span>
                {isActualRole && (
                  <span className="role-badge your-role">
                    Your Role
                  </span>
                )}
                {isSelected && !isActualRole && (
                  <span className="role-badge viewing">
                    Viewing
                  </span>
                )}
              </button>
            );
          })}
          
          {overrideRoles && (
            <div className="role-reset">
              <button
                onClick={() => setOverrideRoles(null)}
                className="role-reset-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Reset to Admin View
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Enhanced Avatar Dropdown
function AvatarDropdown({ profile, showDropdown, setShowDropdown, onLogout, theme, toggleTheme }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowDropdown]);

  const handleDropdownClick = useCallback(() => {
    setShowDropdown(!showDropdown);
  }, [showDropdown, setShowDropdown]);

  const handleProfileClick = useCallback(() => {
    setShowDropdown(false);
  }, [setShowDropdown]);

  const handleBillingClick = useCallback(() => {
    setShowDropdown(false);
  }, [setShowDropdown]);

  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    setShowDropdown(false);
  }, [toggleTheme, setShowDropdown]);

  const handleHelpClick = useCallback(() => {
    setShowDropdown(false);
  }, [setShowDropdown]);

  const handleLogoutClick = useCallback(() => {
    setShowDropdown(false);
    onLogout();
  }, [setShowDropdown, onLogout]);

  const avatarUrl = profile?.avatar_url;
  const initial = profile?.username?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleDropdownClick}
        className="flex items-center gap-3 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="relative">
          {avatarUrl ? (
            <ProfileAvatar 
              avatarUrl={avatarUrl} 
              size="navbar"
              className="ring-2 ring-gray-200 dark:ring-gray-700"
            />
          ) : (
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center ring-2 ring-gray-200 dark:ring-gray-700">
              <span className="text-white font-semibold text-sm">{initial}</span>
            </div>
          )}
        </div>
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <ProfileAvatar 
                  avatarUrl={avatarUrl} 
                  size="medium"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">{initial}</span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {profile?.username || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {profile?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <NavLink
              to="/profile"
              onClick={handleProfileClick}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <User className="w-4 h-4" />
              Profile Settings
            </NavLink>
            
            <NavLink
              to="/billing"
              onClick={handleBillingClick}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Billing & Plans
            </NavLink>

            <button
              onClick={handleThemeToggle}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>

            <NavLink
              to="/help"
              onClick={handleHelpClick}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              Help & Support
            </NavLink>

            <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Navigation Menu
function MobileMenu({ isMobileMenuOpen, setIsMobileMenuOpen, navigationItems, profile, onLogout, theme, toggleTheme, isAdmin, roles, overrideRoles, setOverrideRoles, onlineCount }) {
  const menuRef = useRef(null);

  const handleNavClick = useCallback((path) => {
    setIsMobileMenuOpen(false);
  }, [setIsMobileMenuOpen]);

  const handleProfileClick = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, [setIsMobileMenuOpen]);

  const handleBillingClick = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, [setIsMobileMenuOpen]);

  const handleThemeToggle = useCallback(() => {
    toggleTheme();
    setIsMobileMenuOpen(false);
  }, [toggleTheme, setIsMobileMenuOpen]);

  const handleLogoutClick = useCallback(() => {
    setIsMobileMenuOpen(false);
    onLogout();
  }, [setIsMobileMenuOpen, onLogout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, setIsMobileMenuOpen]);

  if (!isMobileMenuOpen) return null;

  const avatarUrl = profile?.avatar_url;
  const initial = profile?.username?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || 'U';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] lg:hidden" />
      
      {/* Mobile Menu */}
      <div
        ref={menuRef}
        className="fixed top-0 right-0 w-80 max-w-[85vw] h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 z-[1001] lg:hidden transform transition-transform duration-300 ease-out"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <ProfileAvatar 
                avatarUrl={avatarUrl} 
                size="medium"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-semibold">{initial}</span>
              </div>
            )}
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                {profile?.username || 'User'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {profile?.subscription_status || 'Free'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 py-6 overflow-y-auto">
          <nav className="space-y-2 px-6">
            {navigationItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => handleNavClick(to)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Admin Section */}
          {isAdmin && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 px-6">
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Admin Controls</h4>
                
                {/* Mobile Role Selector */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View As:</span>
                    {onlineCount !== null && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="font-medium">{onlineCount} online</span>
                      </div>
                    )}
                  </div>
                  
                  <RoleSelector
                    roles={roles}
                    overrideRoles={overrideRoles}
                    setOverrideRoles={setOverrideRoles}
                    isAdmin={isAdmin}
                  />
                  
                  {overrideRoles && (
                    <button
                      onClick={() => setOverrideRoles(null)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg font-medium transition-colors hover:bg-orange-100 dark:hover:bg-orange-900/30"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset to Admin View
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Settings Section */}
          <div className={`${isAdmin ? 'mt-6' : 'mt-8'} pt-6 border-t border-gray-200 dark:border-gray-800 px-6`}>
            <div className="space-y-2">
              <NavLink
                to="/profile"
                onClick={handleProfileClick}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <User className="w-5 h-5" />
                Profile Settings
              </NavLink>
              
              <NavLink
                to="/billing"
                onClick={handleBillingClick}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <CreditCard className="w-5 h-5" />
                Billing & Plans
              </NavLink>

              <button
                onClick={handleThemeToggle}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}

// Main Enhanced Navbar Component
export default function Navbar({ profile = {}, notifications = [], onLogout = () => {} }) {
  // State management
  const [showDropdown, setShowDropdown] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [onlineCount, setOnlineCount] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Hooks
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { roles = [], overrideRoles, setOverrideRoles, isLoggedIn } = useAuth();

  // Refs (unused refs removed)
  // const mobileMenuRef = useRef(null);

  // Role calculations - Check user's ACTUAL roles, not effective roles
  const { user } = useAuth();
  const isAdminReal = useMemo(() => {
    // Always check the user's actual roles, not the effective roles which can be overridden
    const actualRoles = user?.roles || [];
    const lowers = actualRoles.map(r => String(r).toLowerCase());
    return lowers.includes('admin') || lowers.includes('superadmin');
  }, [user]);

  const SHOW_ADMIN_UI = isAdminReal;

  // Online count fetching for admins
  useEffect(() => {
    if (!isLoggedIn || !isAdminReal) {
      setOnlineCount(null);
      return;
    }

    let cancelled = false;
    const fetchOnline = async () => {
      try {
        const res = await api.get('/admin/online-count');
        if (!cancelled) setOnlineCount(res?.data?.count ?? null);
      } catch {
        if (!cancelled) setOnlineCount(null);
      }
    };

    fetchOnline();
    const interval = setInterval(fetchOnline, 30000);
    return () => { 
      cancelled = true; 
      clearInterval(interval); 
    };
  }, [isLoggedIn, isAdminReal]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Navigation items configuration
  const navigationItems = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/profile', label: 'Profile', icon: User },
    ...(SHOW_ADMIN_UI ? [
      { to: '/admin/dashboard', label: 'Admin Dashboard', icon: BarChart3 },
    ] : [])
  ];

  const handleLogoutClick = useCallback(() => {
    setShowDropdown(false);
    onLogout();
  }, [onLogout]);

  return (
    <>
      {/* Admin Override Banner */}
      {SHOW_ADMIN_UI && overrideRoles && (
        <div className="bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 border-b-2 border-amber-300 dark:border-amber-600">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-center gap-4 text-amber-800 dark:text-amber-200">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                <span className="font-semibold">
                  Viewing app as <span className="font-bold">{overrideRoles[0]}</span>
                </span>
              </div>
              <button
                onClick={() => setOverrideRoles(null)}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 rounded-lg hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors font-medium text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Return to Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Navbar */}
      <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Brand + Navigation */}
            <div className="flex items-center space-x-8">
              {/* Brand */}
              <NavLink 
                to="/dashboard" 
                className="flex items-center space-x-3 text-xl font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">PA</span>
                </div>
                <span className="hidden sm:block">PA Exam</span>
              </NavLink>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-6">
                {navigationItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>


            {/* Right: Actions + Profile - Mobile Optimized */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              
              {/* Admin Role Selector - Always visible */}
              {SHOW_ADMIN_UI && (
                <div className="flex items-center gap-2">
                  <RoleSelector
                    roles={roles}
                    overrideRoles={overrideRoles}
                    setOverrideRoles={setOverrideRoles}
                    isAdmin={isAdminReal}
                  />
                  {/* Always visible reset button when in override mode */}
                  {overrideRoles && (
                    <button
                      onClick={() => setOverrideRoles(null)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                      title="Reset to Admin View"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span className="hidden lg:inline">Reset</span>
                    </button>
                  )}
                </div>
              )}

              {/* Online Count (Admin) - Always visible */}
              {SHOW_ADMIN_UI && onlineCount !== null && (
                <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium" style={{ 
                  backgroundColor: '#10B981', 
                  color: 'white', 
                  borderRadius: '9999px',
                  border: '2px solid #059669',
                  marginLeft: '8px',
                  marginRight: '8px'
                }}>
                  <div className="relative">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <div className="absolute inset-0 w-2 h-2 bg-white rounded-full animate-ping opacity-75" />
                  </div>
                  <span>{onlineCount} online</span>
                </div>
              )}

              {/* Bug Report - Red pill design */}
              <button
                onClick={() => setShowBugModal(true)}
                className="bug-report-btn"
                title="Report Bug"
              >
                <Bug className="w-3 h-3" />
                <span>Bug Report</span>
              </button>

              {/* Notifications - Smaller on mobile */}
              <div className="notification-wrapper">
                <NotificationBell notifications={notifications} />
              </div>

              {/* Desktop Avatar Dropdown */}
              <div className="hidden lg:block">
                <AvatarDropdown
                  profile={profile}
                  showDropdown={showDropdown}
                  setShowDropdown={setShowDropdown}
                  onLogout={handleLogoutClick}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        navigationItems={navigationItems}
        profile={profile}
        onLogout={handleLogoutClick}
        theme={theme}
        toggleTheme={toggleTheme}
        isAdmin={isAdminReal}
        roles={roles}
        overrideRoles={overrideRoles}
        setOverrideRoles={setOverrideRoles}
        onlineCount={onlineCount}
      />

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={showBugModal}
        onClose={() => setShowBugModal(false)}
      />
    </>
  );
}